import { useState, useEffect, useRef } from 'react';
import '../../../entrydoor.css';
import ImageModal from '../../../components/ImageModal';
import useGrillData from './useGrillData';

const GrillPattern = ({
  value,
  onChange,
  id = 'GrillPattern',
  label = 'Window Type:',
  disabled = false,
  showPics = false,
  hideOptions = [],      // Keep for backward compatibility
  grillSizeValue = null,  // Filter patterns by grill size value
  grillTypeValue = null   // Track grill type for cascading reloads
}) => {
  const defaultValue = value?.value || '';
  const [selectedGrillPattern, setSelectedGrillPattern] = useState(defaultValue);
  const [showModal, setShowModal] = useState(false);
  const { getGrillPatterns, getGrillSizeId, getGrillTypeId, loading, error } = useGrillData();
  const prevGrillSizeValue = useRef(grillSizeValue);
  const prevGrillTypeValue = useRef(grillTypeValue);

  // Get grill type ID and size ID from values if provided
  const grillTypeId = grillTypeValue ? getGrillTypeId(grillTypeValue) : undefined;
  const grillSizeId = grillSizeValue ? getGrillSizeId(grillSizeValue, grillTypeId) : undefined;
  let grillPatterns = getGrillPatterns(grillSizeId);

  // Apply hideOptions filter for backward compatibility
  if (hideOptions.length > 0) {
    grillPatterns = grillPatterns.filter(p => !hideOptions.includes(p.value));
  }

  // Update selected value when value prop changes
  useEffect(() => {
    setSelectedGrillPattern(value?.value || '');
  }, [value?.value]);

  // Reset selection when grillSizeValue or grillTypeValue changes
  useEffect(() => {
    const sizeChanged = prevGrillSizeValue.current !== grillSizeValue;
    const typeChanged = prevGrillTypeValue.current !== grillTypeValue;

    if (sizeChanged || typeChanged) {
      prevGrillSizeValue.current = grillSizeValue;
      prevGrillTypeValue.current = grillTypeValue;
      // Force reset to first valid option when grill size or type changes
      if (!loading && grillPatterns.length > 0) {
        const defaultOption = grillPatterns[0];
        const newValue = defaultOption?.value || '';
        const newLabel = defaultOption?.label || '';
        setSelectedGrillPattern(newValue);
        onChange({ value: newValue, label: newLabel });
      }
    }
  }, [grillSizeValue, grillTypeValue, loading, grillSizeId, grillTypeId]);

  // Update selected value when options change (initial load or validation)
  useEffect(() => {
    if (!loading && grillPatterns.length > 0) {
      // Check if current selection is still valid
      const isValidSelection = grillPatterns.some(p => p.value === selectedGrillPattern);
      if (!isValidSelection) {
        const defaultOption = grillPatterns[0];
        const newValue = defaultOption?.value || '';
        const newLabel = defaultOption?.label || '';
        setSelectedGrillPattern(newValue);
        onChange({ value: newValue, label: newLabel });
      }
    }
  }, [loading, grillPatterns.length]);

  const handleGrillPatternChange = (e) => {
    const newValue = e.target.value;
    const selectedText = e.target.options[e.target.selectedIndex].text.replace(' ✔', '');

    setSelectedGrillPattern(newValue);
    onChange({ value: newValue, label: selectedText });
  };

  if (error) {
    console.error('Error loading grill patterns:', error);
  }

  return (
    <div className="a-container">
      <label
        disabled={disabled}
        htmlFor={id}
        onClick={() => showPics && setShowModal(true)}
        style={{ cursor: showPics ? 'pointer' : 'default' }}
      >
        {label}
      </label>

      <select
        disabled={disabled || loading}
        id={id}
        value={selectedGrillPattern}
        onChange={handleGrillPatternChange}
      >
        {loading ? (
          <option value="">Loading...</option>
        ) : (
          grillPatterns.map(option => (
            <option key={option.id} value={option.value}>
              {selectedGrillPattern === option.value ? `${option.label} ✔` : option.label}
            </option>
          ))
        )}
      </select>

      <ImageModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        folder="images/GrillPattern"
        imageName={selectedGrillPattern}
        width={200}
        height={250}
      />
    </div>
  );
};

export default GrillPattern;
